import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { confirmModal } from '@/store/confirmStore';
import { toast } from '@/store/toastStore';
import { useAuthStore } from '@/store/authStore';
import { comma } from '@/lib/format';
import { cn } from '@/lib/cn';

/**
 * 어드민 — 장기 미접속 정책 관리.
 *   - 탭: "예정"(30일 이내 비활성화 임박) / "비활성화 완료"
 *   - super 권한일 때만 수동 복구 + 즉시 실행 버튼 노출
 */

type TabKey = 'upcoming' | 'inactive';
const LIMIT = 30;

interface InactivityRow {
  user_id: string;
  nickname: string;
  original_nickname: string | null;
  last_login_at: string | null;
  inactivated_at: string | null;
  days_remaining: number | null;
  point_balance: number;
}

interface InactivityListResponse {
  rows: InactivityRow[];
  total: number;
  page: number;
  pageSize: number;
}

export function InactivityPage() {
  const me = useAuthStore((s) => s.me);
  const isSuper = me?.adminRole === 'super';

  const qc = useQueryClient();
  const [tab, setTab] = useState<TabKey>('upcoming');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'inactivity', tab, page],
    queryFn: () =>
      get<InactivityListResponse>(`/admin/inactivity?type=${tab}&page=${page}&limit=${LIMIT}`),
    staleTime: 30_000,
  });

  const reactivateMut = useMutation({
    mutationFn: (userId: string) => post<{ nickname: string }>(`/admin/inactivity/${userId}/reactivate`, {}),
    onSuccess: (res) => {
      toast.success(`복구 완료 — 닉네임: ${res.nickname}`);
      qc.invalidateQueries({ queryKey: ['admin', 'inactivity'] });
    },
    onError: () => toast.error('복구에 실패했어요.'),
  });

  const runNowMut = useMutation({
    mutationFn: () =>
      post<{ sent: { sent30: number; sent15: number; sent3: number }; deactivated: number }>(
        '/admin/inactivity/run-now',
        {},
      ),
    onSuccess: (res) => {
      toast.success(
        `안내 ${res.sent.sent30 + res.sent.sent15 + res.sent.sent3}건, 비활성화 ${res.deactivated}건 처리`,
      );
      qc.invalidateQueries({ queryKey: ['admin', 'inactivity'] });
    },
    onError: () => toast.error('실행에 실패했어요.'),
  });

  const rows = data?.rows ?? [];
  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;

  return (
    <div className="w-full">
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold">미접속 계정 관리</h1>
          <p className="mt-1 text-xs text-[var(--color-text-sub)]">
            마지막 로그인 후 1년이 경과하면 매일 03:00 KST 에 비활성화돼요. 잔여 포인트는 소멸되고
            닉네임은 마스킹됩니다.
          </p>
        </div>
        {isSuper && (
          <Button
            size="md"
            variant="secondary"
            loading={runNowMut.isPending}
            onClick={async () => {
              const ok = await confirmModal({
                title: '즉시 실행',
                message: '사전 안내 메일 발송과 비활성화 처리를 지금 실행할까요?',
                confirmLabel: '실행',
                cancelLabel: '취소',
              });
              if (ok) runNowMut.mutate();
            }}
          >
            지금 실행
          </Button>
        )}
      </header>

      {/* 탭 */}
      <div className="mb-5 flex w-full gap-1 rounded-md bg-[var(--color-tab-bg)] p-1">
        {([
          { key: 'upcoming', label: '예정 (30일 이내)' },
          { key: 'inactive', label: '비활성화 완료' },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => {
              setTab(t.key);
              setPage(1);
            }}
            className={cn(
              'flex-1 rounded-md px-3 py-1.5 text-sm transition-colors',
              tab === t.key
                ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                : 'text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)]',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : rows.length === 0 ? (
        <p className="rounded-md border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-sub)]">
          {tab === 'upcoming' ? '비활성화 임박 계정이 없어요.' : '비활성화된 계정이 없어요.'}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg)]/40 text-xs text-[var(--color-text-sub)]">
              <tr>
                <th className="px-3 py-2 text-left">닉네임</th>
                <th className="px-3 py-2 text-left">마지막 로그인</th>
                {tab === 'upcoming' ? (
                  <th className="px-3 py-2 text-right">남은 일수</th>
                ) : (
                  <th className="px-3 py-2 text-left">비활성화 시각</th>
                )}
                <th className="px-3 py-2 text-right">잔여 포인트</th>
                <th className="px-3 py-2 text-right">관리</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {rows.map((r) => (
                <tr key={r.user_id} className="hover:bg-[var(--color-bg)]/40">
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.nickname}</div>
                    {r.original_nickname && (
                      <div className="text-[10px] text-[var(--color-text-sub)]">
                        원래: {r.original_nickname}
                      </div>
                    )}
                    <code className="text-[10px] text-[var(--color-text-sub)]">
                      {r.user_id.slice(0, 8)}…
                    </code>
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-[var(--color-text-sub)] tabular-nums">
                    {r.last_login_at
                      ? new Date(r.last_login_at).toLocaleString('ko-KR', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                        })
                      : '-'}
                  </td>
                  {tab === 'upcoming' ? (
                    <td className="px-3 py-2 text-right tabular-nums">
                      {r.days_remaining !== null && r.days_remaining <= 3 ? (
                        <Badge variant="fall">D-{r.days_remaining}</Badge>
                      ) : (
                        <span className="font-semibold">{r.days_remaining ?? '-'}일</span>
                      )}
                    </td>
                  ) : (
                    <td className="whitespace-nowrap px-3 py-2 text-xs text-[var(--color-text-sub)] tabular-nums">
                      {r.inactivated_at
                        ? new Date(r.inactivated_at).toLocaleString('ko-KR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                          })
                        : '-'}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right font-semibold tabular-nums">
                    {comma(r.point_balance)}P
                  </td>
                  <td className="px-3 py-2 text-right">
                    {tab === 'inactive' && isSuper ? (
                      <Button
                        size="sm"
                        loading={
                          reactivateMut.isPending && reactivateMut.variables === r.user_id
                        }
                        onClick={async () => {
                          const ok = await confirmModal({
                            title: '계정 복구',
                            message: `"${r.nickname}" 계정을 복구할까요?\n닉네임은 "${
                              r.original_nickname ?? r.nickname
                            }"(으)로 환원돼요. 소멸된 포인트는 복구되지 않습니다.`,
                            confirmLabel: '복구',
                            cancelLabel: '취소',
                          });
                          if (ok) reactivateMut.mutate(r.user_id);
                        }}
                      >
                        복구
                      </Button>
                    ) : (
                      <span className="text-xs text-[var(--color-text-sub)]">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > LIMIT && (
        <div className="mt-4 flex items-center justify-center gap-2">
          <Button
            size="sm"
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            이전
          </Button>
          <span className="text-xs text-[var(--color-text-sub)]">
            {page} / {totalPages}
          </span>
          <Button
            size="sm"
            variant="secondary"
            disabled={page >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            다음
          </Button>
        </div>
      )}
    </div>
  );
}
